import { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { OrganizationOverviewData } from '@/shared/types/overviewTypes';
import { DatapoolConsumptionChartContent } from '@/domains/global-dashboard/sections/consumption/DatapoolConsumptionChartContent';
import { FleetEvolutionChart } from '@/domains/global-dashboard/sections/service-lines/FleetEvolutionChart';

export type MarketDashboardDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    organizationOverview: OrganizationOverviewData;
};

/** Market-level dashboard: consumption history and service-line evolution from account overview API data. */
export function MarketDashboardDialog({
    open,
    onOpenChange,
    organizationOverview,
}: MarketDashboardDialogProps) {
    const selectedOrganizations = useMemo(() => [organizationOverview], [organizationOverview]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-7xl max-h-[95vh] flex flex-col gap-4 overflow-hidden p-4">
                <DialogHeader className="shrink-0">
                    <DialogTitle className="text-lg font-semibold">Market Dashboard — {organizationOverview.name}</DialogTitle>
                </DialogHeader>

                <Tabs defaultValue="consumption" className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto w-full">
                    <TabsList className="grid w-full max-w-md grid-cols-2 shrink-0 w-full justify-center mx-auto">
                        <TabsTrigger value="consumption" className="data-[state=active]:bg-foreground data-[state=active]:text-background">Consumption overview</TabsTrigger>
                        <TabsTrigger value="service-lines" className="data-[state=active]:bg-foreground data-[state=active]:text-background">Service lines overview</TabsTrigger>
                    </TabsList>

                    <TabsContent value="consumption" className="mt-0 min-h-0 flex-1 overflow-y-auto focus-visible:outline-none">
                        <DatapoolConsumptionChartContent
                            selectedOrganizations={selectedOrganizations}
                        />
                    </TabsContent>

                    <TabsContent value="service-lines" className="mt-0 min-h-0 flex-1 overflow-y-auto focus-visible:outline-none">
                        <FleetEvolutionChart
                            selectedOrganizations={selectedOrganizations}
                        />
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}
